import { Azing } from './azing';
import { Polymorph } from './polymorph';
import { throwException } from './service-exception';
import { User } from './user';

declare var $2sxc: any;
declare const azing: Azing;

export class DnnUserService {
  private http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> } = window;
  private host: string;
  private headers: Headers;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(host: string) {
    this.host = host;
    this.headers = this.getHeaders();
  }

  private baseUrl(): string {
    // private baseUrl: string = 'http://azing-dnn.dnndev.me/API/2sxc/app/azing-js-user/api/'; // DEV
    // private baseUrl: string = 'https://dev.azing.org/API/2sxc/app/azing-js-user/api/'; // PRODUCTION
    return this.host + '/API/2sxc/app/Users/' + Polymorph.GetLoginApiLocation() + '/api/';
  }

  private getHeaders(): Headers {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');

    /** request verification token for form */
    // try {
    //   const rvt = $2sxc && $2sxc.env && $2sxc.env.rvt();
    //   if (rvt) {
    //     headers.append('RequestVerificationToken', rvt);
    //   }
    // } catch (error) {
    //   console.error('Error with RequestVerificationToken', error);
    // }

    return headers;
  }

  /**
   * @email string
   * @password string
   * @return Promise<string>
   */
  register(email: string, password: string): Promise<boolean> {
    let url = this.baseUrl() + 'Account/Register?';
    if (email)
      url += `email=${encodeURIComponent(`${email}`)}&`;
    if (password)
      url += `password=${encodeURIComponent(`${password}`)}&`;
    url = url.replace(/[?&]$/, '');

    const options = {
      method: 'GET',
      credentials: 'include',
      headers: this.headers,
    } as RequestInit;

    return this.http.fetch(url, options).then((response: Response) => {
      return this.processRegister(response);
    });
  }

  protected processRegister(response: Response): Promise<boolean> {
    const status = response.status;

    const headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => headers[k] = v);
    }

    if (status === 200) {
      return response.text().then((responseText) => {
        const data = responseText === '' ? false : JSON.parse(responseText, this.jsonParseReviver);
        return (data !== undefined) ? data : false;
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((responseText) => {
        return throwException('An unexpected server error occurred.', status, responseText, headers);
      });
    }
    return Promise.resolve<boolean>(null as any);
  }

  /**
   * @return Promise<User>
   */
  currentUser(): Promise<User> {
    const url = this.baseUrl() + 'Account/CurrentUser';

    const options = {
      method: 'GET',
      credentials: 'include',
      headers: this.headers,
    } as RequestInit;

    return this.http.fetch(url, options).then((response: Response) => {
      return this.processCurrentUser(response);
    });
  }

  protected processCurrentUser(response: Response): Promise<User> {
    const status = response.status;

    if (status === 200) {
      return response.text().then((responseText) => {
        const data = responseText === '' ? null : JSON.parse(responseText, this.jsonParseReviver);
        const user = data ? User.fromJS(data) : new User();

        // add new User to current$ observable
        azing.current$.next(user);

        return user;
      });
    } else if (status !== 200 && status !== 204) {
      const headers: any = {};
      if (response.headers && response.headers.forEach) {
        response.headers.forEach((v: any, k: any) => headers[k] = v);
      }

      return response.text().then((responseText) => {
        return throwException('An unexpected server error occurred.', status, responseText, headers);
      });
    }

    return Promise.resolve<User>(null as any);
  }

  /**
   * @username string
   * @password string
   * @return Promise<boolean>
   */
  login(username: string, password: string): Promise<boolean> {
    let url = this.baseUrl() + 'Account/Login?';
    if (username) {
      url += `username=${encodeURIComponent(`${username}`)}&`;
    }
    if (password) {
      url += `password=${encodeURIComponent(`${password}`)}&`;
    }
    url = url.replace(/[?&]$/, '');

    const options = {
      method: 'GET',
      credentials: 'include',
      headers: this.headers,
    } as RequestInit;

    return this.http.fetch(url, options).then((response: Response) => {
      return this.processLogin(response);
    });
  }

  protected processLogin(response: Response): Promise<boolean> {
    const status = response.status;

    const headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => headers[k] = v);
    }

    if (status === 200) {
      return response.text().then((responseText) => {
        const data = responseText === '' ? null : JSON.parse(responseText, this.jsonParseReviver);
        return (data !== undefined) ? data : false;
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((responseText) => {
        return throwException('An unexpected server error occurred.', status, responseText, headers);
      });
    }

    return Promise.resolve<boolean>(null as any);
  }

  /**
   * @return Promise<boolean>
   */
  logout(): Promise<boolean> {
    const url = this.baseUrl() + 'Account/Logout';

    const options = {
      method: 'GET',
      credentials: 'include',
      headers: this.headers,
    } as RequestInit;

    return this.http.fetch(url, options).then((response: Response) => {
      return this.processLogout(response);
    });
  }

  protected async processLogout(response: Response): Promise<boolean> {
    const status = response.status;

    const headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => headers[k] = v);
    }

    if (status === 200) {
      const responseText = await response.text();
      const data = responseText === '' ? null : JSON.parse(responseText, this.jsonParseReviver);
      return (data !== undefined) ? data : false;
    } else if (status !== 200 && status !== 204) {
      const responseText_1 = await response.text();
      return throwException('An unexpected server error occurred.', status, responseText_1, headers);
    }

    return Promise.resolve<boolean>(null as any);
  }

  /**
   * @username string
   * @return Promise<boolean>
   */
  forgot(username: string): Promise<boolean> {
    let url = this.baseUrl() + 'Account/Forgot?';
    if (username) {
      url += `username=${encodeURIComponent(`${username}`)}&`;
    }
    url = url.replace(/[?&]$/, '');

    const options = {
      method: 'GET',
      credentials: 'include',
      headers: this.headers,
    } as RequestInit;

    return this.http.fetch(url, options).then((response: Response) => {
      return this.processForgot(response);
    });
  }

  protected processForgot(response: Response): Promise<boolean> {
    const status = response.status;

    const headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => headers[k] = v);
    }

    if (status === 200) {
      return response.text().then((responseText) => {
        const data = responseText === '' ? null : JSON.parse(responseText, this.jsonParseReviver);
        return (data !== undefined) ? data : false;
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((responseText) => {
        return throwException('An unexpected server error occurred.', status, responseText, headers);
      });
    }

    return Promise.resolve<boolean>(null as any);
  }
}
